<script>

import {axios} from "../../mixins";
import {Alerts} from '../../mixins/Alerts.js';
import Modal from "../Modal.vue";
import moment from 'moment';

moment().format();

export default {

  name: 'OperationsForm',

  components: {Modal},

  mixins: [axios],

  props: {
    resource: {
      required: true,
      type: Object,
    },
    options: {
      required: true,
      type: Object,
    },
    locked: {
      required: false,
      type: Boolean,
      default: false,
    },
    types: {
      required: false,
      type: Object,
    },
    alerts: {
      required: false,
      type: Alerts,
    },
    defaultUnits: {
      required: false,
      type: Object,
      default: {}
    }
  },

  watch: {
    resource() {
      this.processOperation()
    }
  },

  data() {
    return {
      resourceForm: {},
      editableClasses: 'bg-secondary italic rounded border py-2 border-grey-lighter w-full px-3 leading-tight focus:outline-none focus:bg-secondary text-md',
      editableClassesSelect: 'cursor-pointer bg-secondary italic rounded border border-grey-lighter w-full py-2 px-3 leading-tight focus:outline-none focus:ring',
      dataSetToggle: 0,
      submitting: false,
      toggleFields: {
        7: {
          0: ['adjusted_yield', 'adjusted_yield_units'],
          1: ['number_of_cuttings'],
        },
      },
      defaultToZero: {
        3: ['n_application_rate', 'p_application_rate', 'k_application_rate'],
        5: ['herbicide_count', 'insecticide_count', 'fungicide_count', 'other_count'],
        7: ['percent_removal']
      },
      confirm: false,
      percentRemovalDisabled: true,
      validationErrors: [],
      loadingValidation: false,
    };
  },

  filters: {
    defaultZero: function (value) {
      if (value === null) {
        return 0;
      }
      return value;
    },
  },

  computed: {
    pageHeader: function () {
      if (this.isCreating) {
        return 'Add ' + this.options.crop_id[this.resourceForm.crop_id].label + ' Operation to the ' + this.resource.data.year + ' Crop Year';
      }
      return this.resource.data.year + ' ' + this.options.crop_id[this.resourceForm.crop_id].label + ' ' + this.options.operation_type_id[this.resource.data.operation_type_id].label + ' Operation';
    },

    isCreating() {
      return this.resource.id === null
    },

    availableOperationTypes() {
      return this.options.available_operation_type_id || this.options.operation_type_id
    },
  },

  created() {
    window.addEventListener("keydown", this.keyListener);
    this.processOperation()

    if (this.resource.data.has_warning) {
      this.getValidationErrors()
    }
  },

  mounted() {
    window.setInterval(() => {
      this.checkForSubmission()
    }, 1000)
  },

  methods: {
    indicatorMetaFor(attribute, operationTypeId = null) {
      const metaRoot = (this.options && this.options.indicatorMeta) ? this.options.indicatorMeta[attribute] : null;
      if (!metaRoot) return null;
      // If an operationTypeId is provided, prefer exact match; if other op-specific entries exist but not this one, do not fall back.
      if (operationTypeId) {
        if (metaRoot.byOperation && Object.prototype.hasOwnProperty.call(metaRoot.byOperation, operationTypeId)) {
          return metaRoot.byOperation[operationTypeId];
        }
        // If there are any op-specific entries but none for this op, return null to avoid cross-op leakage
        if (metaRoot.byOperation && Object.keys(metaRoot.byOperation).length > 0) {
          return null;
        }
      }
      // Fall back to default only when there are no op-specific entries
      return metaRoot.default || null;
    },
    indicatorIsActive(attribute, operationTypeId = null) {
      const meta = this.indicatorMetaFor(attribute, operationTypeId);
      if (!meta) return true; // default visible when no metadata
      return meta.is_active !== false;
    },
    indicatorIsMappedAndActive(attribute, operationTypeId = null) {
      const meta = this.indicatorMetaFor(attribute, operationTypeId);
      return !!meta && meta.is_active === true;
    },
    indicatorLabel(attribute, fallback, operationTypeId = null) {
      const meta = this.indicatorMetaFor(attribute, operationTypeId);
      const label = meta && meta.label ? meta.label : null;
      return label && label.length ? label : fallback;
    },
    submitResource() {
      this.submitting = true
      let dataToSubmit = {...this.resource}
      dataToSubmit.data = this.resourceForm

      if (this.isCreating) {
        dataToSubmit.table = this.types[this.resourceForm.operation_type_id]
        this.$emit('store', dataToSubmit)
      } else {
        this.$emit('update', dataToSubmit)
      }
    },

    deleteResource() {
      this.$emit('delete', this.resource)
    },

    confirmClose(cancel) {
      this.confirm = false;
      if (!cancel) {
        this.deleteResource()
      }
    },

    processOperation() {
      // Format select fields
      let data = {...this.resource.data};

      // Format Dates
      data.date = moment(this.resource.data.date, "YYYY-MM-DD").format('YYYY-MM-DD')
      data.termination_date = moment(this.resource.data.termination_date, "YYYY-MM-DD").format('YYYY-MM-DD')
      data.end_date = moment(this.resource.data.end_date, "YYYY-MM-DD").format('YYYY-MM-DD')

      // Values that default to zero
      data.n_application_rate = data.n_application_rate | this.defaultZero
      data.p_application_rate = data.p_application_rate | this.defaultZero
      data.k_application_rate = data.k_application_rate | this.defaultZero

      // Toggle to active input set
      let typeToggleFields = this.toggleFields[data.operation_type_id];
      if (typeToggleFields) {
        let op = data
        let toggle = 0;
        for (const [key, set] of Object.entries(typeToggleFields)) {
          for (const element of set) {
            if (op[element]) {
              toggle = key
              break;
            }
          }
        }
        this.dataSetToggle = parseInt(toggle)
      }

      // Crop specific settings
      if (data.crop_id === 15) {
        data.failed_harvest = true
      }

      data = this.setDefaultUnits(data)

      this.resourceForm = data

      if (this.resourceForm.has_warning) {
        this.getValidationErrors()
      }
    },

    setDefaultUnits(data) {
      // Default units management
      if (data.operation_type_id === 7) {
        // HARVEST
        // YIELD LIMITS
        if (this.defaultUnits.yield_limits[data.crop_id]) {
          // Clear any units that don't appear in yield limits, if yield limits are set
          if ((this.defaultUnits.yield_limits[data.crop_id][0]['yield_limits'].length ?? 0) > 0) {
            for (let option in this.options.adjusted_yield_units) {
              if (!this.defaultUnits.yield_limits[data.crop_id][0]['yield_limits'].some(unit => unit.adjusted_yield_units == option)) {
                this.options.adjusted_yield_units[option].local_active = false;
              }
            }
          }
          // If no unit is set, set to default unit, or first yield limit if no default is available
          if (!data.adjusted_yield_units) {
            if (this.defaultUnits.yield_limits[data.crop_id][0]['default_yield_units']) {
              data.adjusted_yield_units = this.defaultUnits.yield_limits[data.crop_id][0]['default_yield_units']
            } else if ((this.defaultUnits.yield_limits[data.crop_id][0]['yield_limits'].length ?? 0) > 0) {
              data.adjusted_yield_units = this.defaultUnits.yield_limits[data.crop_id][0]['yield_limits'][0]['adjusted_yield_units']
            }
          }
        } else {
          for (let option in this.options.adjusted_yield_units) {
              this.options.adjusted_yield_units[option].local_active = true;
          }
        }
        // PERCENT REMOVALS
        if (this.defaultUnits.percent_removals[data.crop_id]) {
          if (data.is_bailed) {
            data.percent_removal = this.defaultUnits.percent_removals[data.crop_id][0].default_percent_removal_bailed
          } else {
            data.percent_removal = this.defaultUnits.percent_removals[data.crop_id][0].default_percent_removal
          }
        } else {
          this.percentRemovalDisabled = false
        }
      }
      if (data.operation_type_id === 2) {
        // MANURE
        if (data.animal) {
          // Rate limits
          if (this.defaultUnits.manure_limits[data.animal]) {
            let limits = this.defaultUnits.manure_limits[data.animal]
            for (let limit of limits) {
              if (limit.liquid_or_solid === data.liquid_or_solid) {
                data.manure_application_rate_units = data.manure_application_rate_units ?? limit.manure_application_rate_units
                data.n_content_units = data.n_content_units ?? limit.n_content_units
                data.p_content_units = data.p_content_units ?? limit.p_content_units
                data.k_content_units = data.k_content_units ?? limit.k_content_units
              }
            }
          }
          // CN Ratios
          if (this.defaultUnits.manure_cn_ratios[data.animal]) {
            let ratios = this.defaultUnits.manure_cn_ratios[data.animal]
            for (let ratio of ratios) {
              if ((ratio.liquid_or_solid === data.liquid_or_solid) && !data.has_c_n_analysis) {
                data.c_n_ratio = ratio.ratio
                data.showRatio = false
              }
            }
          }
        }
      }

      return data
    },

    toggleDataSets(optionsCount) {
      this.dataSetToggle = (this.dataSetToggle + 1) % optionsCount

      // Clear number of cuttings when hiding the option
      if ((this.dataSetToggle % 2) === 0) {
        this.resourceForm.number_of_cuttings = null
      }
    },

    setType() {
      this.resourceForm.implement = null
      this.dataSetToggle = 0
      // Defaults:
      let data = this.resourceForm
      let typeId = data.operation_type_id

      if (typeId && this.defaultToZero.hasOwnProperty(typeId)) {
        let defaultToZero = this.defaultToZero[typeId]
        defaultToZero.forEach(function (element) {
          data[element] = data[element] | this.defaultZero;
        }, data);

        data = this.setDefaultUnits(data)

        this.resourceForm = data
      }
    },

    setDepth(value) {
      if (this.options.implement[value.target.value].label === 'No-till') {
        this.resourceForm.depth = 0
        this.resourceForm.implement = value.target.value;
      }
    },

    checkForSubmission() {
      if (!this.alerts.isEmpty()) {
        this.submitting = false
      }
    },

    ignoreWarnings() {
      this.post('ignore', {type: this.types[this.resourceForm.operation_type_id], id: this.resourceForm.id}).then((response) => {
        this.$emit('loadFieldYears');
        this.resourceForm.has_warning = false
        this.validationErrors = []
      })
    },

    getValidationErrors() {
      if(this.validationErrors.length === 0){
        if (this.resourceForm.has_warning) {
          this.loadingValidation = true
          this.$emit('validate', {
            reference: 'operationsForm',
            type: this.types[this.resourceForm.operation_type_id],
            id: this.resourceForm.id
          })
        } else {
          this.setMessages([['Data is valid.']])
        }
      }
    },

    setMessages(messages) {
      this.validationErrors = messages
      this.loadingValidation = false
    },

    needsCuttings(crop) {
      console.log(this.options.crop_id)
      return this.options.crop_id[crop]['has_cuttings']
    }
  },
};

</script>

<template>
  <form @submit.prevent="submitResource" method="post">
    <div class="p-4 w-full">

      <!--   PAGE HEADER   -->
      <div class="font-semibold text-2xl mb-2">
        {{ pageHeader }}
      </div>

      <!--   CREATION TEXT   -->
      <div v-show="isCreating" class="border-t my-2">
        <slot name="create-operation"></slot>
      </div>

      <!--   FORM   -->
      <div class="flex flex-col w-full">
        <!--   MESSAGES   -->
        <div v-if="alerts.getMessage()" class="text-green">
          <i class="fas fa-check-circle mr-2"></i>
          {{ alerts.getMessage() }}
        </div>

        <!--   TYPE SELECT   -->
        <div v-if="isCreating" class="flex flex-row flex-wrap flex-1 items-center my-1">
          <label for="operation_type_id" class="font-semibold">Select Operation Type*</label>

          <select name="operation_type_id"
                  id="operation_type_id"
                  v-model="resourceForm.operation_type_id"
                  :disabled="locked"
                  :class="editableClassesSelect"
                  @change="setType" required>
            <option :value="null">Select an operation Type</option>
            <option v-for="option in availableOperationTypes" :value="option.value">
              {{ option.label }}
            </option>
          </select>

          <div class="text-red">
            {{ alerts.getError('operation_type_id') }}
          </div>
        </div>

        <!--   HELP TEXTS  -->
        <div>
          <p class="mb-2">
            <span v-if="resourceForm.operation_type_id && resourceForm.operation_type_id===1">
                        <slot name="tillage"></slot>
            </span>
            <span v-else-if="resourceForm.operation_type_id && resourceForm.operation_type_id===2">
                        <slot name="manure"></slot>
            </span>
            <span v-else-if="resourceForm.operation_type_id && resourceForm.operation_type_id===3">
                        <slot name="fertilizer"></slot>
            </span>
            <span v-else-if="resourceForm.operation_type_id && resourceForm.operation_type_id===4">
                        <slot name="planting"></slot>
            </span>
            <span v-else-if="resourceForm.operation_type_id && resourceForm.operation_type_id===5">
                        <slot name="chemical"></slot>
            </span>
            <span v-else-if="resourceForm.operation_type_id && resourceForm.operation_type_id===6">
                        <slot name="irrigation"></slot>
            </span>
            <span v-else-if="resourceForm.operation_type_id && resourceForm.operation_type_id===7">
                        <slot name="harvest"></slot>
            </span>
            <span v-else-if="resourceForm.operation_type_id && resourceForm.operation_type_id===8">
                        <slot name="cover-crops"></slot>
            </span>
            <span v-else-if="resourceForm.operation_type_id && resourceForm.operation_type_id===9">
                        <slot name="grazing"></slot>
            </span>
            <span v-else-if="resourceForm.operation_type_id && resourceForm.operation_type_id===10">
                    <slot name="forage-management"></slot>
            </span>
          </p>
        </div>

        <!--   DATE   -->
        <div class="flex flex-col flex-wrap flex-1 text-left my-1">
          <label for="date" class="font-semibold">{{
              resourceForm.operation_type_id === 8 ? 'Approximate Planting Date'
                  : resourceForm.operation_type_id === 6 ? 'Approximate Start Date'
                      : 'Approximate Date'
            }}*</label>
          <input type="date" id="date" name="date"
                 v-model="resourceForm.date"
                 :disabled="locked"
                 aria-label="Date"
                 class="cursor-pointer"
                 :class="editableClasses"
                 :min="(this.resource.data.year - ((resourceForm.operation_type_id &&
          [4, 8, 9].includes(resourceForm.operation_type_id) && resourceForm.crop_id && [12, 26, 13, 20,
          14, 5].includes(resourceForm.crop_id))? 10 : 1)) + '-01-01'"
                 :max="(this.resource.data.year + ((resourceForm.operation_type_id &&
          [4, 8, 9].includes(resourceForm.operation_type_id) && resourceForm.crop_id && [12, 26, 13, 20,
          14, 5].includes(resourceForm.crop_id))? 10 : 1)) + '-01-01'">
          <div class="text-red">
            {{ alerts.getError('date') }}
          </div>
        </div>

        <!--   CROP   -->
        <div class="flex flex-row flex-wrap flex-1 items-center my-1">
          <select name="crop_id"
                  id="crop_id"
                  v-model="resourceForm.crop_id"
                  :class="editableClassesSelect"
                  ref="cropSelector"
                  hidden>
            <option :value="null">Select an Option</option>
            <template v-for="option in options.crop_id">
              <option v-if="(option.local_active === true)
                || (option.value === resourceForm.crop_id)"
                :value="option.value">{{ option.label }}
              </option>
            </template>
          </select>
        </div>

        <!--   TYPE SPECIFIC FIELDS   -->
        <div v-if="resourceForm.operation_type_id">
          <!-- Tillage -->
          <template v-if="resourceForm.operation_type_id===1">
            <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('implement', resourceForm.operation_type_id)">

              <label for="implement" class="font-semibold">{{ indicatorLabel('implement', 'Tillage Implement*', resourceForm.operation_type_id) }}</label>

              <select name="implement"
                      id="implement"
                      v-model="resourceForm.implement"
                      :disabled="locked"
                      :class="editableClassesSelect"
                      @input="setDepth">
                <option :value="null">Select Implement</option>
                <template v-for="option in options.implement">
                  <option v-if="(option.local_active === true && option.is_active === true)
                    || (option.value === resourceForm.implement)"
                    :value="option.value">{{ option.label }}
                  </option>
                </template>
              </select>

              <div class="text-red">
                {{ alerts.getError('implement') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('depth', resourceForm.operation_type_id)">
              <label for="depth" class="font-semibold">{{ indicatorLabel('depth', 'Tillage Depth (inches)*', resourceForm.operation_type_id) }}</label>
<!---->
              <input v-model="resourceForm.depth"
                     name="depth"
                     :disabled="locked"
                     :class="editableClasses"
                     type="number"
                     min="0"
                     :placeholder="(this.resourceForm.implement && this.options.implement[this.resourceForm.implement]?.label) === 'No-till' ? 0 : ''"
                     :readonly="(this.resourceForm.implement && this.options.implement[this.resourceForm.implement]?.label) === 'No-till'"
                     step=".01">
              <div class="text-red">
                {{ alerts.getError('depth') }}
              </div>
            </div>
          </template>

          <!-- Manure -->
          <div v-else-if="resourceForm.operation_type_id===2">
            <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('animal', resourceForm.operation_type_id)">
              <label for="animal" class="font-semibold">{{ indicatorLabel('animal', 'Animal Source*', resourceForm.operation_type_id) }}</label>

              <select name="animal"
                      id="animal"
                      v-model="resourceForm.animal"
                      :disabled="locked"
                      :class="editableClassesSelect"
                      @change="this.setDefaultUnits(this.resourceForm)">
                <template v-for="option in options.animal">
                  <option v-if="(option.local_active === true && option.is_active === true)
                    || (option.value === resourceForm.animal)"
                    :value="option.value">{{ option.label }}
                  </option>
                </template>
              </select>

              <div class="text-red">
                {{ alerts.getError('animal') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('liquid_or_solid', resourceForm.operation_type_id)">
              <label for="liquid_or_solid" class="font-semibold">{{ indicatorLabel('liquid_or_solid', 'Liquid or Solid*', resourceForm.operation_type_id) }}</label>
              <select name="liquid_or_solid"
                      id="liquid_or_solid"
                      v-model="resourceForm.liquid_or_solid"
                      :disabled="locked"
                      :class="editableClassesSelect"
                      @change="this.setDefaultUnits(this.resourceForm)">
                <option :value="true">Liquid</option>
                <option :value="false">Solid</option>
              </select>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('pit_or_pile', resourceForm.operation_type_id)">
              <label for="pit_or_pile" class="font-semibold">{{ indicatorLabel('pit_or_pile', 'Pit or Pile', resourceForm.operation_type_id) }}</label>
              <select name="pit_or_pile"
                      id="pit_or_pile"
                      v-model="resourceForm.pit_or_pile"
                      :disabled="locked"
                      :class="editableClassesSelect">
                <option :value="true">Pit</option>
                <option :value="false">Pile</option>
              </select>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('inhibitor', resourceForm.operation_type_id)">
              <label for="inhibitor" class="font-semibold">{{ indicatorLabel('inhibitor', 'Fertilizer Inhibitor', resourceForm.operation_type_id) }}</label>
              <select name="inhibitor"
                      id="inhibitor"
                      v-model="resourceForm.inhibitor"
                      :disabled="locked"
                      :class="editableClassesSelect">
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('multiple_applications', resourceForm.operation_type_id)">
              <label for="multiple_applications" class="font-semibold">{{ indicatorLabel('multiple_applications', 'Multiple Applications*', resourceForm.operation_type_id) }}</label>
              <select name="multiple_applications"
                      id="multiple_applications"
                      v-model="resourceForm.multiple_applications"
                      :disabled="locked"
                      :class="editableClassesSelect">
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>

            <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('application_method', resourceForm.operation_type_id)">
              <label for="application_method" class="font-semibold">{{ indicatorLabel('application_method', 'Application Method*', resourceForm.operation_type_id) }}</label>

              <select name="application_method"
                      id="application_method"
                      v-model="resourceForm.application_method"
                      :disabled="locked"
                      :class="editableClassesSelect">
                <template v-for="option in options.application_method">
                  <option v-if="(option.local_active === true && option.is_active === true)
                    || (option.value === resourceForm.application_method)"
                    :value="option.value">{{ option.label }}
                  </option>
                </template>
              </select>

              <div class="text-red">
                {{ alerts.getError('application_method') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1">
              <label class="font-semibold">
                <input v-model="resourceForm.has_c_n_analysis" type="checkbox"
                       name="has_c_n_analysis" value="0" class="mr-1">
                  C/N Analysis Available
              </label>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('c_n_ratio', resourceForm.operation_type_id)">
              <label for="c_n_ratio"
                     class="font-semibold"
                     :hidden="!resourceForm.has_c_n_analysis"
              >
                {{ indicatorLabel('c_n_ratio', 'Manure C-N Ratio*', resourceForm.operation_type_id) }}
              </label>

              <input v-model="resourceForm.c_n_ratio"
                     name="c_n_ratio"
                     :hidden="!resourceForm.has_c_n_analysis"
                     :disabled="locked"
                     :class="editableClasses"
                     type="number"
                     min="0"
                     step="0.01"
              >
              <div class="text-red">
                {{ alerts.getError('c_n_ratio') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1">
              <label for="k_application_rate" class="font-semibold">Application Depth (inches)*</label>

              <input name="application_depth"
                     :disabled="locked"
                     :class="editableClasses"
                     type="number"
                     :value="resourceForm.application_depth"
                     @input="resourceForm.application_depth = $event.target.value"
                     min="0"
                     step=".01">
              <div class="text-sm italic">
                Enter 0 for surface application
              </div>
              <div class="text-red">
                {{ alerts.getError('application_depth') }}
              </div>
            </div>

            <div class="flex flex-col md:flex-row">
              <div class="w-full md:w-1/2 md:pr-2">
                <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('application_rate', resourceForm.operation_type_id)">
                  <label for="application_rate" class="font-semibold">{{ indicatorLabel('application_rate', 'Application Rate*', resourceForm.operation_type_id) }}</label>

                  <input v-model="resourceForm.application_rate"
                         name="application_rate"
                         :disabled="locked"
                         :class="editableClasses"
                         type="number"
                         min="0"
                         step=".1">
                  <div class="text-red">
                    {{ alerts.getError('application_rate') }}
                  </div>
                </div>
              </div>

              <div class="w-full md:w-1/2 md:pl-2 md:ml-0">
                <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('manure_application_rate_units', resourceForm.operation_type_id)">
                  <label for="manure_application_rate_units" class="font-semibold">{{ indicatorLabel('manure_application_rate_units', 'Application Rate\n                      Units*', resourceForm.operation_type_id) }}</label>

                  <select name="manure_application_rate_units"
                          id="manure_application_rate_units"
                          v-model="resourceForm.manure_application_rate_units"
                          :class="editableClassesSelect">
                    <template v-for="option in options.manure_application_rate_units">
                      <option v-if="(option.local_active === true && option.is_active === true)
                        || (option.value === resourceForm.manure_application_rate_units)"
                        :value="option.value">{{ option.label }}
                      </option>
                    </template>
                  </select>
                  <div class="text-red">
                    {{ alerts.getError('manure_application_rate_units') }}
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col md:flex-row">

              <div class="w-full md:w-1/2 md:pr-2">
                <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('n_content', resourceForm.operation_type_id)">
                  <label for="n_content" class="font-semibold">{{ indicatorLabel('n_content', 'Manure N Content*', resourceForm.operation_type_id) }}</label>

                  <input v-model="resourceForm.n_content"
                         name="n_content"
                         :disabled="locked"
                         :class="editableClasses"
                         type="number"
                         min="0">
                  <div class="text-red">
                    {{ alerts.getError('n_content') }}
                  </div>
                </div>
              </div>

              <div class="w-full md:w-1/2 md:pl-2 md:ml-0">
                <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('n_content_units', resourceForm.operation_type_id)">
                  <label for="n_content_units" class="font-semibold">{{ indicatorLabel('n_content_units', 'Units*', resourceForm.operation_type_id) }}</label>

                  <select name="n_content_units"
                          id="n_content_units"
                          :disabled="locked"
                          v-model="resourceForm.n_content_units"
                          :class="editableClassesSelect">
                    <template v-for="option in options.n_content_units">
                      <option v-if="(option.local_active === true && option.is_active === true)
                        || (option.value === resourceForm.n_content_units)"
                        :value="option.value">{{ option.label }}
                      </option>
                    </template>
                  </select>

                  <div class="text-red">
                    {{ alerts.getError('n_content_units') }}
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col md:flex-row">
              <div class="w-full md:w-1/2 md:pr-2">
                <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('p_content', resourceForm.operation_type_id)">
                  <label for="p_content" class="font-semibold">{{ indicatorLabel('p_content', 'Manure P Content (P2O5)*', resourceForm.operation_type_id) }}</label>

                  <input v-model="resourceForm.p_content"
                         name="p_content"
                         :disabled="locked"
                         :class="editableClasses"
                         type="number"
                         min="0">
                  <div class="text-red">
                    {{ alerts.getError('p_content') }}
                  </div>
                </div>
              </div>

              <div class="w-full md:w-1/2 md:pl-2 md:ml-0">
                <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('p_content_units', resourceForm.operation_type_id)">
                  <label for="p_content_units" class="font-semibold">{{ indicatorLabel('p_content_units', 'Units*', resourceForm.operation_type_id) }}</label>

                  <select name="p_content_units"
                          id="p_content_units"
                          v-model="resourceForm.p_content_units"
                          :disabled="locked"
                          :class="editableClassesSelect">
                    <template v-for="option in options.p_content_units">
                      <option v-if="(option.local_active === true && option.is_active === true)
                        || (option.value === resourceForm.p_content_units)"
                        :value="option.value">{{ option.label }}
                      </option>
                    </template>
                  </select>

                  <div class="text-red">
                    {{ alerts.getError('p_content_units') }}
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col md:flex-row">
              <div class="w-full md:w-1/2 md:pr-2">
                <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('k_content', resourceForm.operation_type_id)">
                  <label for="k_content" class="font-semibold">{{ indicatorLabel('k_content', 'Manure K Content (K2O)*', resourceForm.operation_type_id) }}</label>

                  <input v-model="resourceForm.k_content"
                         name="k_content"
                         :disabled="locked"
                         :class="editableClasses"
                         type="number"
                         min="0">
                  <div class="text-red">
                    {{ alerts.getError('k_content') }}
                  </div>
                </div>
              </div>

              <div class="w-full md:w-1/2 md:pl-2 md:ml-0">
                <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('k_content_units', resourceForm.operation_type_id)">
                  <label for="k_content_units" class="font-semibold">{{ indicatorLabel('k_content_units', 'Units*', resourceForm.operation_type_id) }}</label>

                  <select name="k_content_units"
                          id="k_content_units"
                          :disabled="locked"
                          v-model="resourceForm.k_content_units"
                          :class="editableClassesSelect">
                    <template v-for="option in options.k_content_units">
                      <option v-if="(option.local_active === true && option.is_active === true)
                        || (option.value === resourceForm.k_content_units)"
                        :value="option.value">{{ option.label }}
                      </option>
                    </template>
                  </select>

                  <div class="text-red">
                    {{ alerts.getError('k_content_units') }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Fertilizer -->
          <div v-else-if="resourceForm.operation_type_id===3">
            <div class="flex flex-row flex-wrap flex-1 items-center my-1"
                 v-if="options.implement_type && indicatorIsMappedAndActive('implement_type', resourceForm.operation_type_id)">
              <label for="implement_type" class="font-semibold">{{ indicatorLabel('implement_type', 'Implement Type', resourceForm.operation_type_id) }}</label>

              <select name="implement_type"
                      id="implement_type"
                      v-model="resourceForm.implement_type"
                      :class="editableClassesSelect">
                <template v-for="option in options.implement_type">
                  <option v-if="(option.local_active === true && option.is_active === true)
                    || (option.value === resourceForm.implement_type)"
                          :value="option.value">{{ option.label }}
                  </option>
                </template>
              </select>

              <div class="text-red">
                {{ alerts.getError('implement_type') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1"
                 v-if="indicatorIsMappedAndActive('fertilizer_inhibitor', resourceForm.operation_type_id)">
              <label for="fertilizer_inhibitor" class="font-semibold">{{ indicatorLabel('fertilizer_inhibitor', 'Nitrification Inhibitor', resourceForm.operation_type_id) }}</label>

              <select name="inhibitor"
                      id="fertilizer_inhibitor"
                      v-model="resourceForm.inhibitor"
                      :class="editableClassesSelect">
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>

              <div class="text-red">
                {{ alerts.getError('inhibitor') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('n_application_rate', resourceForm.operation_type_id)">
              <label for="n_application_rate" class="font-semibold">{{ indicatorLabel('n_application_rate', 'Nitrogen (N) Application Rate\n                (lbs/acre)*', resourceForm.operation_type_id) }}</label>

              <input v-model="resourceForm.n_application_rate"
                     name="n_application_rate"
                     :disabled="locked"
                     :class="editableClasses"
                     type="number"
                     min="0"
                     step=".1"
              >
              <div class="text-red">
                {{ alerts.getError('n_application_rate') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('p_application_rate', resourceForm.operation_type_id)">
              <label for="p_application_rate" class="font-semibold">{{ indicatorLabel('p_application_rate', 'Phosphorus (P) Application Rate\n                (lbs/acre)*', resourceForm.operation_type_id) }}</label>

              <input v-model="resourceForm.p_application_rate"
                     name="p_application_rate"
                     :disabled="locked"
                     :class="editableClasses"
                     type="number"
                     min="0"
                     step=".1"
              >
              <div class="text-red">
                {{ alerts.getError('p_application_rate') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('k_application_rate', resourceForm.operation_type_id)">
              <label for="k_application_rate" class="font-semibold">{{ indicatorLabel('k_application_rate', 'Potassium (K) Application Rate\n                (lbs/acre)*', resourceForm.operation_type_id) }}</label>

              <input v-model="resourceForm.k_application_rate"
                     name="k_application_rate"
                     :disabled="locked"
                     :class="editableClasses"
                     type="number"
                     min="0"
                     step=".1"
              >
              <div class="text-red">
                {{ alerts.getError('k_application_rate') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('application_depth', resourceForm.operation_type_id)">
              <label for="application_depth" class="font-semibold">{{ indicatorLabel('application_depth', 'Application Depth (inches)*', resourceForm.operation_type_id) }}</label>

              <input name="application_depth"
                     :disabled="locked"
                     :class="editableClasses"
                     type="number"
                     :value="resourceForm.application_depth"
                     @input="resourceForm.application_depth = $event.target.value"
                     min="0"
                     step=".01">
              <div class="text-sm italic">
                Enter 0 for surface application
              </div>
              <div class="text-red">
                {{ alerts.getError('application_depth') }}
              </div>
            </div>
          </div>

          <!-- Planting -->
          <div v-else-if="resourceForm.operation_type_id===4">
            <div class="flex flex-row flex-wrap flex-1 my-1">
              <label class="font-semibold">
                <input v-model="resourceForm.failed_planting" type="checkbox"
                       name="failed_planting" value="1" class="mr-1">
                Failed Planting
              </label>
              <span class="pl-1">: {{ resourceForm.failed_planting ? 'True' : 'False' }}</span>
            </div>

            <div v-if="!resourceForm.failed_planting">

              <div class="flex flex-row flex-wrap flex-1 items-center my-1"
                   v-if="options.planter_type && indicatorIsMappedAndActive('planter_type', resourceForm.operation_type_id)">
                <label for="planter_type" class="font-semibold">{{ indicatorLabel('planter_type', 'Planter Type', resourceForm.operation_type_id) }}</label>

                <select name="planter_type"
                        id="planter_type"
                        v-model="resourceForm.planter_type"
                        :class="editableClassesSelect">
                  <template v-for="option in options.planter_type">
                    <option v-if="(option.local_active === true && option.is_active === true)
                      || (option.value === resourceForm.planter_type)"
                            :value="option.value">{{ option.label }}
                    </option>
                  </template>
                </select>

                <div class="text-red">
                  {{ alerts.getError('planter_type') }}
                </div>
              </div>

              <div class="flex flex-row flex-wrap flex-1 items-center my-1"
                   v-if="resourceForm.crop_id === 13 && indicatorIsActive('dominant_hay_species', resourceForm.operation_type_id)">
                <label for="dominant_hay_species" class="font-semibold">{{ indicatorLabel('dominant_hay_species', 'Dominant Hay Species', resourceForm.operation_type_id) }}</label>

                <select name="dominant_hay_species"
                        id="dominant_hay_species"
                        v-model="resourceForm.dominant_hay_species"
                        :class="editableClassesSelect">
                  <template v-for="option in options.dominant_hay_species">
                    <option v-if="(option.local_active === true && option.is_active === true)
                      || (option.value === resourceForm.dominant_hay_species)"
                      :value="option.value">{{ option.label }}
                    </option>
                  </template>
                </select>

                <div class="text-red">
                  {{ alerts.getError('dominant_hay_species') }}
                </div>
              </div>

              <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('seeding_rate', resourceForm.operation_type_id)">
                <label for="seeding_rate" class="font-semibold">{{ indicatorLabel('seeding_rate', 'Seeding Rate (seeds/ac)*', resourceForm.operation_type_id) }}</label>

                <input v-model="resourceForm.seeding_rate"
                       name="seeding_rate"
                       :disabled="locked"
                       :class="editableClasses"
                       type="number"
                       min="0">
                <div class="text-red">
                  {{ alerts.getError('seeding_rate') }}
                </div>
              </div>

              <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('row_spacing', resourceForm.operation_type_id)">
                <label for="row_spacing" class="font-semibold">{{ indicatorLabel('row_spacing', 'Row Spacing (inches)*', resourceForm.operation_type_id) }}</label>

                <input v-model="resourceForm.row_spacing"
                       name="row_spacing"
                       :disabled="locked"
                       :class="editableClasses"
                       type="number"
                       min="0">
                <div class="text-red">
                  {{ alerts.getError('row_spacing') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Chemical -->
          <div v-else-if="resourceForm.operation_type_id===5">
            <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('herbicide_count', resourceForm.operation_type_id)">
              <label for="herbicide_count" class="font-semibold">{{ indicatorLabel('herbicide_count', 'Number of Herbicide Products used in Pass*', resourceForm.operation_type_id) }}</label>

              <input name="herbicide_count"
                     id="herbicide_count"
                     v-model="resourceForm.herbicide_count"
                     :disabled="locked"
                     :class="editableClassesSelect"
                     :max="6"
                     :min="0"
                     placeholder="0"
                     type="number"
              >

              <div class="text-red">
                {{ alerts.getError('herbicide_count') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('insecticide_count', resourceForm.operation_type_id)">
              <label for="insecticide_count" class="font-semibold">{{ indicatorLabel('insecticide_count', 'Number of Insecticide Products used in Pass*', resourceForm.operation_type_id) }}</label>

              <input name="insecticide_count"
                     id="insecticide_count"
                     v-model="resourceForm.insecticide_count"
                     :disabled="locked"
                     :class="editableClassesSelect"
                     :max="4"
                     :min="0"
                     placeholder="0"
                     type="number"
              >

              <div class="text-red">
                {{ alerts.getError('insecticide_count') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('fungicide_count', resourceForm.operation_type_id)">
              <label for="fungicide_count" class="font-semibold">{{ indicatorLabel('fungicide_count', 'Number of Fungicide Products used in Pass*', resourceForm.operation_type_id) }}</label>

              <input name="fungicide_count"
                     id="fungicide_count"
                     v-model="resourceForm.fungicide_count"
                     :disabled="locked"
                     :class="editableClassesSelect"
                     :max="4"
                     :min="0"
                     placeholder="0"
                     type="number"
              >

              <div class="text-red">
                {{ alerts.getError('fungicide_count') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('other_count', resourceForm.operation_type_id)">
              <label for="other_count" class="font-semibold">{{ indicatorLabel('other_count', 'Number of Other Products used in Pass*', resourceForm.operation_type_id) }}</label>

              <input name="other_count"
                     id="other_count"
                     v-model="resourceForm.other_count"
                     :disabled="locked"
                     :class="editableClassesSelect"
                     :max="4"
                     :min="0"
                     placeholder="0"
                     type="number"
              >

              <div class="text-red">
                {{ alerts.getError('other_count') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('chemical_application_method', resourceForm.operation_type_id)">
              <label for="application_method" class="font-semibold">{{ indicatorLabel('chemical_application_method', 'Application Method', resourceForm.operation_type_id) }}</label>
              <select name="application_method"
                      id="application_method"
                      v-model="resourceForm.application_method"
                      :class="editableClassesSelect">
                <template v-for="option in options.application_methods">
                  <option v-if="(option.local_active === true && option.is_active === true)
                      || (option.value === resourceForm.application_method)"
                          :value="option.value">{{ option.label }}
                  </option>
                </template>
              </select>

              <div class="text-red">
                {{ alerts.getError('application_method') }}
              </div>
            </div>

            <div>
              *Product application should follow label instructions
            </div>
          </div>

          <!-- Irrigation -->
          <div v-else-if="resourceForm.operation_type_id===6">
            <div class="flex flex-col md:flex-row">
              <div class="w-full md:w-1/2 md:pr-2">
                <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('freshwater_amount', resourceForm.operation_type_id)">
                  <label for="freshwater_amount" class="font-semibold">{{ indicatorLabel('freshwater_amount', 'Freshwater Amount*', resourceForm.operation_type_id) }}</label>

                  <input v-model="resourceForm.freshwater_amount"
                         name="freshwater_amount"
                         :disabled="locked"
                         :class="editableClasses"
                         type="number"
                         min="0"
                         step=".1">
                </div>

                <div class="text-red">
                  {{ alerts.getError('freshwater_amount') }}
                </div>
              </div>

              <div class="w-full md:w-1/2 md:pl-2 md:ml-0">
                <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('freshwater_amount_units', resourceForm.operation_type_id)">
                  <label for="freshwater_amount_units" class="font-semibold">{{ indicatorLabel('freshwater_amount_units', 'Freshwater Amount\n                    Units*', resourceForm.operation_type_id) }}</label>

                  <select name="freshwater_amount_units"
                          id="freshwater_amount_units"
                          v-model="resourceForm.freshwater_amount_units"
                          :class="editableClassesSelect">
                    <template v-for="option in options.freshwater_amount_units">
                      <option v-if="(option.local_active === true && option.is_active === true)
                        || (option.value === resourceForm.freshwater_amount_units)"
                        :value="option.value">{{ option.label }}
                      </option>
                    </template>
                  </select>

                  <div class="text-red">
                    {{ alerts.getError('freshwater_amount_units') }}
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('method', resourceForm.operation_type_id)">
              <label for="method" class="font-semibold">{{ indicatorLabel('method', 'Method*', resourceForm.operation_type_id) }}</label>

              <select name="method"
                      id="method"
                      v-model="resourceForm.method"
                      :class="editableClassesSelect">
                <template v-for="option in options.method">
                  <option v-if="(option.local_active === true && option.is_active === true)
                    || (option.value === resourceForm.method)"
                    :value="option.value">{{ option.label }}
                  </option>
                </template>
              </select>

              <div class="text-red">
                {{ alerts.getError('method') }}
              </div>
            </div>
          </div>

          <!-- Harvest -->
          <div v-else-if="resourceForm.operation_type_id===7">
            <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('harvest_type_id', resourceForm.operation_type_id)">
              <label for="harvest_type_id" class="font-semibold">{{ indicatorLabel('harvest_type_id', 'Harvest Type*', resourceForm.operation_type_id) }}</label>

              <select name="harvest_type_id"
                      id="harvest_type_id"
                      v-model="resourceForm.harvest_type_id"
                      :class="editableClassesSelect">
                <template v-for="option in options.harvest_type_id">
                  <option v-if="(option.local_active === true && option.is_active === true)
                            || (option.value === resourceForm.harvest_type_id)"
                          :value="option.value">{{ option.label }}
                  </option>
                </template>
              </select>

              <div class="text-red">
                {{ alerts.getError('harvest_type_id') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1"
                 v-if="indicatorIsMappedAndActive('aph_yield', resourceForm.operation_type_id)">
              <label for="aph_yield" class="font-semibold">{{ indicatorLabel('aph_yield', 'APH Yield', resourceForm.operation_type_id) }}</label>

              <input v-model="resourceForm.aph_yield"
                     name="aph_yield"
                     :disabled="locked"
                     :class="editableClasses"
                     type="number"
                     min="0"
                     step=".1">
              <div class="text-red">
                {{ alerts.getError('aph_yield') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1"
                 v-if="indicatorIsMappedAndActive('moisture', resourceForm.operation_type_id)">
              <label for="moisture" class="font-semibold">{{ indicatorLabel('moisture', 'Moisture (%)', resourceForm.operation_type_id) }}</label>

              <input v-model="resourceForm.moisture"
                     name="moisture"
                     :disabled="locked"
                     :class="editableClasses"
                     type="number"
                     min="0"
                     max="100"
                     step=".1">
              <div class="text-red">
                {{ alerts.getError('moisture') }}
              </div>
            </div>

            <div v-if="resourceForm.harvest_type_id === 4075">
              <div class="flex flex-col md:flex-row">
                <div class="w-full md:w-1/2 md:pr-2">
                  <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('adjusted_yield', resourceForm.operation_type_id)">
                    <label for="adjusted_yield" class="font-semibold">{{ indicatorLabel('adjusted_yield', 'Adjusted Yield*', resourceForm.operation_type_id) }}</label>

                    <input v-model="resourceForm.adjusted_yield"
                           name="adjusted_yield"
                           :disabled="locked"
                           :class="editableClasses"
                           type="number"
                           min="0">

                    <div class="text-red">
                      {{ alerts.getError('adjusted_yield') }}
                    </div>
                  </div>
                </div>

                <div class="w-full md:w-1/2 md:pl-2 md:ml-0">
                  <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('adjusted_yield_units', resourceForm.operation_type_id)">
                    <label for="adjusted_yield_units_harvests" class="font-semibold">{{ indicatorLabel('adjusted_yield_units', 'Adjusted Yield\n                      Units*', resourceForm.operation_type_id) }}</label>

                    <select name="adjusted_yield_units"
                            id="adjusted_yield_units_harvests"
                            v-model="resourceForm.adjusted_yield_units"
                            :class="editableClassesSelect">
                      <template v-for="option in options.adjusted_yield_units">
                        <option v-if="(option.local_active === true && option.is_active === true)
                          || (option.value === resourceForm.adjusted_yield_units)"
                          :value="option.value">{{ option.label }}
                        </option>
                      </template>
                    </select>

                    <div class="text-red">
                      {{ alerts.getError('adjusted_yield_units') }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex flex-row flex-wrap flex-1 my-1" v-if="needsCuttings(resourceForm.crop_id) && indicatorIsActive('number_of_cuttings', resourceForm.operation_type_id)">
                <label for="number_of_cuttings" class="font-semibold">{{ indicatorLabel('number_of_cuttings', 'Number of Cuttings*', resourceForm.operation_type_id) }}</label>

                <input v-model="resourceForm.number_of_cuttings"
                       name="number_of_cuttings"
                       :disabled="locked"
                       :class="editableClasses"
                       type="number"
                       min="0"
                       step=".1">
                <div class="text-red">
                  {{ alerts.getError('number_of_cuttings') }}
                </div>
              </div>

              <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('percent_removal', resourceForm.operation_type_id)">
                <label for="percent_removal" class="font-semibold">{{ indicatorLabel('percent_removal', 'Percent Crop Residue Removed', resourceForm.operation_type_id) }}</label>

                <input v-model="resourceForm.percent_removal"
                       name="percent_removal"
                       :disabled="this.percentRemovalDisabled"
                       :class="editableClasses"
                       type="number"
                       min="0"
                       max="100">
                <div class="text-red">
                  {{ alerts.getError('percent_removal') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Cover Crop -->
          <div v-else-if="resourceForm.operation_type_id===8">
            <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('cover_crop_species', resourceForm.operation_type_id)">
              <label for="cover_crop_species" class="font-semibold">{{ indicatorLabel('cover_crop_species', 'Primary Cover Crop Species', resourceForm.operation_type_id) }}</label>

              <select name="cover_crop_species"
                      id="cover_crop_species"
                      v-model="resourceForm.cover_crop_species"
                      :class="editableClassesSelect"
                      ref="coverCropSelector">
                <option :value="null">Select an Option</option>
                <template v-for="option in options.cover_crops_crop_id">
                  <option v-if="(option.local_active === true) || (option.value === resourceForm.cover_crop_species)"
                          :value="option.value">{{ option.label }}
                  </option>
                </template>
              </select>
              <div class="text-red">
                {{ alerts.getError('cover_crop_species') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('failed_planting', resourceForm.operation_type_id)">
              <label class="font-semibold">
                <input v-model="resourceForm.failed_planting" type="checkbox"
                       name="failed_planting" value="1" class="mr-1">
                {{ indicatorLabel('failed_planting', 'Failed Planting', resourceForm.operation_type_id) }}
              </label>
              <span class="pl-1">: {{ resourceForm.failed_planting ? 'True' : 'False' }}</span>
            </div>

            <div v-if="!resourceForm.failed_planting">
              <div class="flex flex-row flex-wrap flex-1 my-1">
                <label for="seeding_rate_lbs" class="font-semibold">Seeding Rate (lbs/ac)*</label>

                <input v-model="resourceForm.seeding_rate_lbs"
                       name="seeding_rate_lbs"
                       :disabled="locked"
                       :class="editableClasses"
                       type="number"
                       min="0">
                <div class="text-red">
                  {{ alerts.getError('seeding_rate_lbs') }}
                </div>
              </div>

              <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('multi_species', resourceForm.operation_type_id)">
                <label class="font-semibold">
                  <input v-model="resourceForm.multi_species" type="checkbox"
                         name="multi_species" value="1" class="mr-1">
                  {{ indicatorLabel('multi_species', 'Multi-Species', resourceForm.operation_type_id) }}
                </label>
                <span class="pl-1">: {{ resourceForm.multi_species ? 'True' : 'False' }}</span>
              </div>

              <div v-if="resourceForm.multi_species">
                <div class="flex flex-row flex-wrap flex-1 items-center my-1">
                  <template v-if="indicatorIsActive('secondary_cover_crop_species', resourceForm.operation_type_id)">
                    <label for="secondary_cover_crop_species" class="font-semibold">{{ indicatorLabel('secondary_cover_crop_species', 'Secondary Cover Crop Species', resourceForm.operation_type_id) }}</label>

                    <select name="secondary_cover_crop_species"
                            id="secondary_cover_crop_species"
                            v-model="resourceForm.secondary_cover_crop_species"
                            :class="editableClassesSelect"
                            ref="secondary_cover_crop_species">
                      <option :value="null">Select an Option</option>
                      <template v-for="option in options.cover_crops_crop_id">
                        <option v-if="(option.local_active === true) || (option.value === resourceForm.secondary_cover_crop_species)"
                                :value="option.value">{{ option.label }}
                        </option>
                      </template>
                    </select>
                  </template>

                  <div class="flex flex-row flex-wrap flex-1 my-1">
                    <label for="secondary_seeding_rate" class="font-semibold">Secondary Seeding Rate (lbs/ac)</label>

                    <input v-model="resourceForm.secondary_seeding_rate"
                           name="secondary_seeding_rate"
                           :disabled="locked"
                           :class="editableClasses"
                           type="number"
                           min="0">
                    <div class="text-red">
                      {{ alerts.getError('secondary_seeding_rate') }}
                    </div>
                  </div>
                </div>

                <div class="flex flex-row flex-wrap flex-1 items-center my-1">
                  <template v-if="indicatorIsActive('tertiary_cover_crop_species', resourceForm.operation_type_id)">
                    <label for="tertiary_cover_crop_species" class="font-semibold">{{ indicatorLabel('tertiary_cover_crop_species', 'Tertiary Cover Crop Species', resourceForm.operation_type_id) }}</label>

                    <select name="tertiary_cover_crop_species"
                            id="tertiary_cover_crop_species"
                            v-model="resourceForm.tertiary_cover_crop_species"
                            :class="editableClassesSelect"
                            ref="tertiary_cover_crop_species">
                      <option :value="null">Select an Option</option>
                      <template v-for="option in options.cover_crops_crop_id">
                        <option v-if="(option.local_active === true) || (option.value === resourceForm.tertiary_cover_crop_species)"
                                :value="option.value">{{ option.label }}
                        </option>
                      </template>
                    </select>
                  </template>

                  <div class="flex flex-row flex-wrap flex-1 my-1">
                    <label for="tertiary_seeding_rate" class="font-semibold">Tertiary Seeding Rate (lbs/ac)</label>

                    <input v-model="resourceForm.tertiary_seeding_rate"
                           name="tertiary_seeding_rate"
                           :disabled="locked"
                           :class="editableClasses"
                           type="number"
                           min="0">
                    <div class="text-red">
                      {{ alerts.getError('tertiary_seeding_rate') }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex flex-row flex-wrap flex-1 items-center my-1">
                <label for="planting_method" class="font-semibold">Planting Method</label>

                <select name="planting_method"
                        id="planting_method"
                        v-model="resourceForm.planting_method"
                        :class="editableClassesSelect">
                  <option v-for="option in options.planting_method" :value="option">{{ option }}</option>
                </select>

                <div class="text-red">
                  {{ alerts.getError('planting_method') }}
                </div>
              </div>

              <div class="flex flex-col flex-wrap flex-1 text-left my-1">
                <label for="termination_date" class="font-semibold">Termination Date</label>
                <input type="date" id="termination_date" name="termination_date"
                       v-model="resourceForm.termination_date"
                       :disabled="locked"
                       aria-label="Termination Date"
                       :class="editableClasses"
                       :min="(this.resource.data.year - 1) + '-01-01'" :max="(this.resource.data.year + 1) + '-12-31'">

                <div class="text-red">
                  {{ alerts.getError('termination_date') }}
                </div>
              </div>

              <div class="flex flex-row flex-wrap flex-1 my-1">
                <label for="termination_technique" class="font-semibold">Termination Technique</label>

                <select name="termination_technique"
                        id="termination_technique"
                        v-model="resourceForm.termination_technique"
                        :class="editableClassesSelect">

                  <option v-for="option in options.termination_technique" :value="option">
                    {{ option }}
                  </option>
                </select>

                <div class="text-red">
                  {{ alerts.getError('termination_technique') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Grazing -->
          <div v-else-if="resourceForm.operation_type_id===9">
            <div class="flex flex-col flex-wrap flex-1 text-left my-1">
              <label for="end_date" class="font-semibold">End Date*</label>
              <input type="date" id="end_date" name="end_date"
                     v-model="resourceForm.end_date"
                     :disabled="locked"
                     aria-label="End Date"
                     :class="editableClasses"
                     :min="(this.resource.data.year - 1) + '-01-01'" :max="(this.resource.data.year + 2) + '-01-01'">

              <div class="text-red">
                {{ alerts.getError('end_date') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('animal_type', resourceForm.operation_type_id)">
              <label for="animal_type" class="font-semibold">{{ indicatorLabel('animal_type', 'Animal Type*', resourceForm.operation_type_id) }}</label>

              <select name="animal_type"
                      id="animal_type"
                      v-model="resourceForm.animal_type"
                      :class="editableClassesSelect">
                <template v-for="option in options.animal_type">
                  <option v-if="(option.local_active === true && option.is_active === true)
                    || (option.value === resourceForm.animal_type)"
                    :value="option.value">{{ option.label }}
                  </option>
                </template>
              </select>

              <div class="text-red">
                {{ alerts.getError('animal_type') }}
              </div>
            </div>

            <div class="flex flex-row flex-wrap flex-1 my-1" v-if="indicatorIsActive('animal_quantity', resourceForm.operation_type_id)">
              <label for="animal_quantity" class="font-semibold">{{ indicatorLabel('animal_quantity', 'Animal Quantity (head/ac)', resourceForm.operation_type_id) }}</label>

              <input v-model="resourceForm.animal_quantity"
                     name="animal_quantity"
                     :disabled="locked"
                     :class="editableClasses"
                     type="number"
                     min="0"
                     step="1">
              <div class="text-red">
                {{ alerts.getError('animal_quantity') }}
              </div>
            </div>
          </div>

          <!-- ForageManagement -->
          <div v-else-if="resourceForm.operation_type_id===10">
            <div class="flex flex-row flex-wrap flex-1 items-center my-1" v-if="indicatorIsActive('forage_management', resourceForm.operation_type_id)">
              <label for="forage_management" class="font-semibold">{{ indicatorLabel('forage_management', ' Forage Activity*', resourceForm.operation_type_id) }}</label>
              <select name="forage_management"
                      id="forage_management"
                      v-model="resourceForm.forage_management"
                      :class="editableClassesSelect">
                <template v-for="option in options.forage_management">
                  <option v-if="(option.local_active === true && option.is_active === true)
                    || (option.value === resourceForm.forage_management)"
                    :value="option.value">{{ option.label }}
                  </option>
                </template>
              </select>

              <div class="text-red">
                {{ alerts.getError('forage_management') }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="resourceForm.has_warning"
             class="rounded bg-yellow border border-yellow-dark text-center">
          <!-- WARNING DIALOG -->
          <template v-if="loadingValidation">
            <div class="text-center">
              <span class="mr-2"><i class="fas fa-spinner fa-spin"></i></span> Loading Errors...
            </div>
          </template>
          <template v-else>
            <ul>
              <li v-for="error in validationErrors" class="mb-1">
                Warning! {{ error[0] }}
              </li>
            </ul>
          </template>
          <button @click="ignoreWarnings()"
                  type="button"
                  class="mx-auto mb-1 w-auto button bg-primary border-blue text-blue text-sm hover:bg-grey-lightest focus:bg-grey-lighter">
            Confirm & Ignore
          </button>
        </div>

        <!--   ACTION BUTTONS   -->
        <div class="text-sm flex flex-row-reverse justify-between">
          <div v-if="this.submitting"
               class="button button-wide bg-blue text-white hover:bg-blue-dark mt-2 font-bold">
            <span class="fas fa-spinner-third fa-spin"></span>
          </div>

          <button
              v-show="!this.submitting"
              :disabled="this.submitting || this.locked"
              class="button button-wide bg-blue text-white hover:bg-blue-dark focus:bg-blue-darker mt-2 font-bold disabled:cursor-not-allowed"
              type="submit" name="field-submit">
            <span class="mr-1"><i class="fas fa-floppy-disk"/></span>
            <span>Save</span>
          </button>

          <button
              v-if="!this.isCreating"
              :disabled="this.submitting || this.locked"
              class="button button-wide bg-red text-white hover:bg-red-dark focus:bg-red-darker mt-2 font-bold disabled:cursor-not-allowed"
              type="button" @click="this.confirm = true">
            <span class="mr-1"><i class="fas fa-trash"/></span>
            <span>Delete</span>
          </button>
        </div>
      </div>

      <div v-if="this.confirm" class="fixed flex justify-center items-center top-0 bottom-0 left-0 right-0" @click="this.confirm = false">
        <div class="bg-primary p-4 w-1/6 text-center rounded tile-shadow" @click.stop="">
          <div>Are you sure you wish to delete this operation?</div>
          <div class="flex justify-between">
            <button class="button button-wide text-grey-dark hover:bg-grey-lightest focus:bg-grey-lighter mt-2 font-bold" @click="confirmClose(true)">
              Cancel
            </button>
            <button class="button button-wide text-white bg-red hover:bg-red-dark focus:bg-red-darker mt-2 font-bold" @click="confirmClose(false)">
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>
